import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm, Space, Row, Col } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { customerApi, type CustomerDTO, type CustomerAddressDTO } from '../api/kfc';
import CustomerImportModal from '../components/CustomerImportModal';
import { clientTablePagination } from '../utils/tablePagination';

const CustomerManage: React.FC = () => {
  const [list, setList] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    customerApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载客户列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ addresses: [{}] });
    setModalOpen(true);
  };

  const openEdit = (r: CustomerDTO) => {
    if (!r.id) return;
    setEditingId(r.id);
    customerApi
      .get(r.id)
      .then((res) => {
        const full = res.data;
        form.setFieldsValue({
          ...full,
          addresses: full.addresses?.length ? full.addresses : [{}],
        });
        setModalOpen(true);
      })
      .catch(() => message.error('加载客户详情失败'));
  };

  const onFinish = (values: Partial<CustomerDTO> & { addresses?: CustomerAddressDTO[] }) => {
    const { addresses, ...rest } = values;
    const payload = {
      ...rest,
      addresses:
        addresses?.filter(
          (a) =>
            a.label ||
            a.contactName ||
            a.companyName ||
            a.email ||
            a.address ||
            a.phone ||
            a.remark ||
            a.postalCode
        ) ?? [],
    };
    const submit = editingId
      ? customerApi.update(editingId, payload)
      : customerApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const doDelete = (id: string) => {
    customerApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const downloadTemplate = () => {
    customerApi
      .downloadTemplate()
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kfy-customer-list.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('模板下载失败'));
  };

  return (
    <>
      <Card
        title="客户列表"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              导入客户
            </Button>
            <Button type="primary" onClick={openAdd}>
              新增客户
            </Button>
          </Space>
        }
      >
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '客户标识', dataIndex: 'customerCode', width: 120 },
            { title: '客户名称', dataIndex: 'name', width: 140 },
            { title: '域名', dataIndex: 'domain', width: 140, ellipsis: true },
            { title: '邮箱', dataIndex: 'email', width: 160, ellipsis: true },
            { title: '电话', dataIndex: 'phone', width: 120 },
            {
              title: '操作',
              width: 180,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定删除该客户吗？"
                    onConfirm={() => r.id != null && doDelete(r.id)}
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑客户' : '新增客户'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="customerCode" label="客户标识" rules={[{ required: true, message: '请输入客户标识' }]}>
            <Input placeholder="唯一标识" disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="domain" label="域名">
            <Input placeholder="为空则使用 {客户标识}.com" />
          </Form.Item>
          <Form.Item name="name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="客户邮箱">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="客户电话">
            <Input />
          </Form.Item>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>客户地址（可选）</div>
          <Form.List name="addresses">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          {...rest}
                          name={[name, 'label']}
                          label="地址标识"
                          rules={[{ required: true, message: '请输入地址标识' }]}
                        >
                          <Input placeholder="地址标识" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'remark']} label="备注">
                          <Input placeholder="备注" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'contactName']} label="联系人">
                          <Input placeholder="联系人" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'companyName']} label="公司名称">
                          <Input placeholder="公司名称" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'email']} label="邮箱">
                          <Input type="email" placeholder="邮箱" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'phone']} label="电话">
                          <Input placeholder="电话" />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item {...rest} name={[name, 'address']} label="地址">
                          <Input placeholder="地址" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...rest} name={[name, 'postalCode']} label="邮编">
                          <Input placeholder="邮编" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="text" danger onClick={() => remove(name)}>
                      删除此地址
                    </Button>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + 添加地址
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <CustomerImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} />
    </>
  );
};

export default CustomerManage;
